import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import AppCell from '@/components/common/app-cell';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import Linkable from '@/components/common/linkable';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { HeadImageUploadButton } from '@/components/mine/head-image-upload-button';
import { useBrand } from '@/hooks/pwa/use-brand';
import useUserPlatform from '@/hooks/use-user-platform';
import useCancelAccount from '@/hooks/users/use-cancel-account';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';

export default function MyInfoRoute() {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const { isEmbedded } = useUserPlatform();
  const { data: userInfo, isLoading } = useUserInfo();
  const { mutateAsync: cancelAccount } = useCancelAccount();

  const handleDeleteAccount = async () => {
    if (
      await makePrompt({
        key: 'delete-account',
        title: t('personal-info.delete-account.title'),
        content: t('personal-info.delete-account.alert'),
      })
    ) {
      const res = await cancelAccount();

      if (res?.Code === 0) {
        navigate(ROUTES.AUTH.SIGN_IN.$path());
      }
    }
  };

  return (
    <PageLayout
      title={t('mine.personal-info')}
      headerConfig={{
        navigateBack: true,
      }}
      blockOnDesktop>
      <LoadingLayout promise={isLoading}>
        {() => (
          <div className='mx-1 flex flex-col'>
            <HeadImageUploadButton>
              <AppCell
                label={
                  <AvatarWithStatus
                    className='size-12'
                    size={[48, 48]}
                    imageUrl={userInfo?.HeadImg}
                  />
                }
                chevron={!isEmbedded}
              />
            </HeadImageUploadButton>

            <Linkable
              linkable={!isEmbedded}
              to={ROUTES.MY.NICKNAME.$path()}>
              <AppCell
                label={t('personal-info.nickname')}
                content={userInfo?.NickName ?? ''}
                chevron={!isEmbedded}
              />
            </Linkable>

            <Link to={ROUTES.MY.SIGNATURE.$path()}>
              <AppCell
                label={t('personal-info.signature')}
                content={<span className='truncate'>{userInfo?.Sign ?? ''}</span>}
                chevron
              />
            </Link>

            <hr className='m-2' />

            {!isEmbedded && (
              <AppCell
                content={userInfo ? `+${userInfo.TelCode} ${userInfo.Phone}` : ''}
                label={t('personal-info.phone')}
              />
            )}

            <AppCell
              content={<span className='font-mono'>{userInfo?.VChatID ?? ''}</span>}
              label={t('user.chat-id', { brandShortName: brand.shortName })}
              onClick={() => {
                if (userInfo?.VChatID) {
                  navigator.clipboard.writeText(userInfo.VChatID);
                  addToast({ type: 'success', message: t('common.copy-success') });
                }
              }}
            />

            <Link to={ROUTES.MY.QR_CODE.$path()}>
              <AppCell
                label={t('personal-info.qr-code')}
                chevron
              />
            </Link>

            <hr className='m-2 mode-embedded:hidden' />

            <AppCell
              label={t('personal-info.delete-account.title')}
              labelClassName='text-red-500 mode-embedded:hidden'
              onClick={handleDeleteAccount}
            />
          </div>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
